import type { Plan } from "./types";

/**
 * Rendering a tier for an operator.
 *
 * Both helpers lived inline before — `formatMemory` copied into PlansPage and
 * open-coded again in AppDetailPage — which is survivable for a format function
 * and not for the rule below, since the two pages would disagree about what a
 * plan is called.
 */

export function formatMemory(mb: number): string {
  return mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB` : `${mb} MB`;
}

/** The canonical one-line rendering of a tier's enforced resources. */
export function planSpec(plan: Plan): string {
  return `${plan.resources.cpu} vCPU · ${formatMemory(plan.resources.memoryMb)}`;
}

/**
 * A plan in one line: its name, and its resources when the name omits them.
 *
 * Current tier names *are* the specification — "2 vCPU · 4 GB" — so appending
 * the same two figures would print each twice. The retired tiers carry
 * adjectives instead ("Small"), and anything still running on those needs the
 * numbers spelled out. Comparing against the canonical rendering rather than
 * sniffing the string keeps that exact.
 */
export function describePlan(plan: Plan): string {
  const spec = planSpec(plan);
  return plan.name === spec ? spec : `${plan.name} · ${spec}`;
}
